import { apiService, API_ENDPOINTS, buildEndpoint } from '@/shared/services/api'
import { API_BASE_URL } from '@/shared/services/api/api.config'
import Cookies from 'js-cookie'
import type { EnrollmentSession } from '@/features/enrollment/types'

export interface SaveStepRequest {
  sessionId?: string
  stepId: string
  stepIndex: number
  stepData: Record<string, any>
  // enrollmentType is optional - will be determined automatically based on user role
  enrollmentType?: 'CLIENT_ENROLLMENT' | 'SELF_ENROLLMENT'
}

export class EnrollmentApi {
  static async saveStepAuto(payload: SaveStepRequest) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      API_ENDPOINTS.enrollment.saveStepAuto,
      payload
    )

    return res.data.data
  }

  static async getSession(sessionId: string) {
    const res = await apiService.get<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint(API_ENDPOINTS.enrollment.getSession, { sessionId })
    )
    return res.data.data
  }


  static async checkVerification(sessionId: string) {
    // Use public verify endpoint to check status without altering anything
    const res = await apiService.get<{ success: boolean; data: { emailLinkVerified: boolean; emailLinkVerifiedAt?: string; clientIp?: string } }>(
      `/enrollments/public/sessions/${sessionId}/verify`
    )
    return res.data.data
  }

  static async listSessions(filters?: {
    status?: string;
    stage?: number;
    // enrollmentType is optional - can be used to filter results
    enrollmentType?: 'CLIENT_ENROLLMENT' | 'SELF_ENROLLMENT';
    campaignId?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) {
    const params = new URLSearchParams()

    if (filters?.status) {
      params.append('status', filters.status)
    }

    if (filters?.enrollmentType) {
      params.append('enrollmentType', filters.enrollmentType)
    }

    if (filters?.stage !== undefined) {
      params.append('stage', filters.stage.toString())
    }

    if (filters?.campaignId) {
      params.append('campaignId', filters.campaignId)
    }

    if (filters?.page) {
      params.append('page', filters.page.toString())
    }

    if (filters?.limit) {
      params.append('limit', filters.limit.toString())
    }

    if (filters?.search) {
      params.append('search', filters.search)
    }

    const queryString = params.toString()
    const url = queryString ? `${API_ENDPOINTS.enrollment.listSessions}?${queryString}` : API_ENDPOINTS.enrollment.listSessions

    const res = await apiService.get<{
      success: boolean;
      data: EnrollmentSession[];
      pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      }
    }>(url)

    return {
      sessions: res.data.data,
      pagination: res.data.pagination
    }
  }

  static async sendStep2Email(sessionId: string, toEmail: string) {
    const res = await apiService.post<{ success: boolean }>(
      buildEndpoint(API_ENDPOINTS.enrollment.sendEmailLink, { sessionId }),
      { toEmail }
    )
    return res.data
  }

  static async sendFollowUpEmail(sessionId: string, toEmail: string) {
    const res = await apiService.post<{ success: boolean }>(
      buildEndpoint('/enrollments/sessions/:sessionId/send-followup', { sessionId }),
      { toEmail }
    )
    return res.data
  }

  static async sendIdReminderEmail(sessionId: string, toEmail: string) {
    const res = await apiService.post<{ success: boolean }>(
      buildEndpoint('/enrollments/sessions/:sessionId/send-id-reminder', { sessionId }),
      { toEmail }
    )
    return res.data
  }

  static async moveToPending(sessionId: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint(API_ENDPOINTS.enrollment.moveToPending, { sessionId })
    )
    return res.data.data
  }

  static async resume(sessionId: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint(API_ENDPOINTS.enrollment.resume, { sessionId })
    )
    return res.data.data
  }

  static async submit(sessionId: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint(API_ENDPOINTS.enrollment.submit, { sessionId })
    )
    return res.data.data
  }

  static async markAsSigned(sessionId: string, signatureData?: {
    signedAt?: string;
    signatureMethod?: string;
    clientConfirmed?: boolean;
  }) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/signed', { sessionId }),
      signatureData || {}
    )
    return res.data.data
  }

  static async complete(sessionId: string, clientId?: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint(API_ENDPOINTS.enrollment.complete, { sessionId }),
      clientId ? { clientId } : undefined
    )
    return res.data.data
  }

  static async getSignedIdentityUrl(sessionId: string) {
    const res = await apiService.get<{ success: boolean; data: { signedUrl: string; expiresAt: string | null } }>(
      buildEndpoint('/enrollments/sessions/:sessionId/identity-url', { sessionId })
    )
    return res.data.data
  }

  static async getSignedDocumentUrl(sessionId: string, documentType: string, options?: { fileUrl?: string }) {
    const endpoint = buildEndpoint('/enrollments/sessions/:sessionId/signed-document-url', { sessionId })
    const params = new URLSearchParams({ documentType })

    if (options?.fileUrl) {
      params.append('fileUrl', options.fileUrl)
    }

    const url = `${endpoint}?${params.toString()}`
    const res = await apiService.get<{ success: boolean; data: { signedUrl: string; expiresAt: string | null; documentType: string } }>(url)
    return res.data.data
  }

  static async sendSelfEnrollmentEmail(clientEmail: string, agentName: string) {
    const res = await apiService.post<{ success: boolean; data: { sessionId: string; message: string } }>(
      API_ENDPOINTS.enrollment.sendSelfEnrollmentEmail,
      { clientEmail, agentName }
    )
    return res.data.data
  }

  static async createUserAccountForReferral(email: string, campaignId?: string, firstName?: string, lastName?: string) {
    const payload = { email, campaignId, firstName, lastName }

    const res = await apiService.post<{ success: boolean; data: { userId: string; email: string; message: string } }>(
      '/enrollments/create-user-account',
      payload
    )

    return res.data.data
  }

  static async validateStepAccess(sessionId: string, targetStepIndex: number) {
    const res = await apiService.post<{
      success: boolean;
      data?: { allowed: boolean; sessionStatus: string };
      error?: string;
      message?: string;
      allowedSteps?: number[];
    }>(
      buildEndpoint('/enrollments/sessions/:sessionId/validate-step-access', { sessionId }),
      { targetStepIndex }
    )
    return res.data
  }

  static async submitOptInForm(sessionId: string) {
    const res = await apiService.post<{
      success: boolean;
      data: {
        success: boolean;
        status: 'success' | 'failed';
        message: string;
        submittedAt: string | null;
        responseData: any;
      };
      error?: string;
    }>(
      buildEndpoint('/enrollments/sessions/:sessionId/opt-in-submit', { sessionId })
    )
    return res.data
  }

  static async submitRobloxForm(robloxFormData: any) {

    const res = await apiService.post<{
      success: boolean;
      data: {
        session: EnrollmentSession;
        robloxSubmission: {
          success: boolean;
          status: 'success' | 'failed';
          message: string;
          submittedAt: string | null;
          responseData: any;
        };
      };
      message: string;
    }>(
      API_ENDPOINTS.enrollment.submitRobloxForm,
      robloxFormData
    )

    return res.data
  }

  static async submitACAForm(sessionId: string, enrollmentData: any) {
    const res = await apiService.post<{
      success: boolean;
      data: any;
    }>(
      buildEndpoint(API_ENDPOINTS.enrollment.submitJotForm, { sessionId }),
      enrollmentData
    )

    return res.data
  }

  static async sendSmsConsent(sessionId: string, enrollmentData: any) {
    const res = await apiService.post<{
      success: boolean;
      data: any;
    }>(
      buildEndpoint(API_ENDPOINTS.enrollment.sendSmsConsent, { sessionId }),
      enrollmentData
    )

    return res.data.data
  }

  static async getSmsConsentStatus(sessionId: string) {
    const res = await apiService.get<{
      success: boolean;
      data: any;
    }>(
      buildEndpoint(API_ENDPOINTS.enrollment.getSmsConsentStatus, { sessionId })
    )

    return res.data.data
  }

  static async downloadSmsConsentProof(sessionId: string) {
    const token = Cookies.get('token')
    const response = await fetch(
      `${API_BASE_URL}${buildEndpoint(API_ENDPOINTS.enrollment.downloadSmsConsentProof, { sessionId })}`,
      {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      }
    )

    if (!response.ok) {
      throw new Error('Failed to download SMS consent proof')
    }

    return response.blob()
  }


  static async getFunnelStats(campaignId?: string) {
    const params = new URLSearchParams();
    if (campaignId) params.append('campaignId', campaignId);

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.enrollment.funnelStats}?${queryString}` : API_ENDPOINTS.enrollment.funnelStats;

    const res = await apiService.get<{ success: boolean; data: any }>(url);
    return res.data.data;
  }

  static async getFunnelClients(step: number, campaignId?: string, page?: number, limit?: number) {
    const params = new URLSearchParams();
    params.append('step', step.toString());
    if (campaignId) params.append('campaignId', campaignId);
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.enrollment.funnelClients}?${queryString}` : API_ENDPOINTS.enrollment.funnelClients;

    const res = await apiService.get<{ success: boolean; data: any }>(url);
    return res.data.data;
  }

  static async getApplicationStateStats(campaignId?: string) {
    const params = new URLSearchParams();
    if (campaignId) params.append('campaignId', campaignId);

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.enrollment.applicationStateStats}?${queryString}` : API_ENDPOINTS.enrollment.applicationStateStats;

    const res = await apiService.get<{ success: boolean; data: any }>(url);
    return res.data.data;
  }

  static async getApplicationStateClients(applicationState: string, campaignId?: string, page?: number, limit?: number) {
    const params = new URLSearchParams();
    params.append('applicationState', applicationState);
    if (campaignId) params.append('campaignId', campaignId);
    if (page) params.append('page', page.toString());
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = queryString ? `${API_ENDPOINTS.enrollment.applicationStateClients}?${queryString}` : API_ENDPOINTS.enrollment.applicationStateClients;

    const res = await apiService.get<{ success: boolean; data: any }>(url);
    return res.data.data;
  }

  /**
   * Update funnel status manually (agent override)
   */
  static async updateFunnelStatus(
    sessionId: string,
    targetStep: number,
    options?: { reason?: string; isExcommunicado?: boolean; excommunicadoReason?: string }
  ) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/funnel-status', { sessionId }),
      { targetStep, ...options }
    );
    return res.data.data;
  }

  /**
   * Update application state (licensed agent's post-submission review status)
   */
  static async updateApplicationState(sessionId: string, applicationState: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/application-state', { sessionId }),
      { applicationState }
    );
    return res.data.data;
  }

  /**
   * List applications for a licensed agent's review queue (New / In Progress / Completed / Rejected)
   */
  static async listLicensedAgentApplications(
    tab: 'new' | 'in-progress' | 'completed' | 'rejected',
    filters?: { campaignId?: string; search?: string; page?: number; limit?: number }
  ) {
    const params = new URLSearchParams();
    params.append('tab', tab);
    if (filters?.campaignId) params.append('campaignId', filters.campaignId);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const res = await apiService.get<{ success: boolean; data: any }>(
      `/enrollments/licensed-agent-applications?${params.toString()}`
    );
    return res.data.data;
  }

  /**
   * Claim an unclaimed application (licensed agent review pipeline)
   */
  static async claimApplication(sessionId: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/claim', { sessionId }),
      {}
    );
    return res.data.data;
  }

  /**
   * Mark a claimed application as completed
   */
  static async completeReview(sessionId: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/complete-review', { sessionId }),
      {}
    );
    return res.data.data;
  }

  /**
   * Reject a claimed application
   */
  static async rejectReview(sessionId: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/reject-review', { sessionId }),
      {}
    );
    return res.data.data;
  }

  /**
   * List applications for a Tort Application Review Agent's review queue
   * (New / In Progress / Completed / Rejected) — mirrors listLicensedAgentApplications
   * but scoped to Tort campaigns (Milestone 3 data-isolation requirement).
   */
  static async listTortReviewAgentApplications(
    tab: 'new' | 'in-progress' | 'completed' | 'rejected',
    filters?: { campaignId?: string; search?: string; page?: number; limit?: number }
  ) {
    const params = new URLSearchParams();
    params.append('tab', tab);
    if (filters?.campaignId) params.append('campaignId', filters.campaignId);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const res = await apiService.get<{ success: boolean; data: any }>(
      `/enrollments/tort-review-agent-applications?${params.toString()}`
    );
    return res.data.data;
  }

  /**
   * Claim an unclaimed application (Tort Application Review Agent pipeline)
   */
  static async claimTortApplication(sessionId: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/claim-tort', { sessionId }),
      {}
    );
    return res.data.data;
  }

  /**
   * Mark a claimed Tort application as completed
   */
  static async completeTortReview(sessionId: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/complete-tort-review', { sessionId }),
      {}
    );
    return res.data.data;
  }

  /**
   * Reject a claimed Tort application
   */
  static async rejectTortReview(sessionId: string) {
    const res = await apiService.patch<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/reject-tort-review', { sessionId }),
      {}
    );
    return res.data.data;
  }

  /**
   * Set excommunicado flag (blacklist client)
   */
  static async setExcommunicado(sessionId: string, reason: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/excommunicado', { sessionId }),
      { reason }
    );
    return res.data.data;
  }

  /**
   * Remove excommunicado flag
   */
  static async removeExcommunicado(sessionId: string) {
    const res = await apiService.delete<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/excommunicado', { sessionId })
    );
    return res.data.data;
  }

  /**
   * Assign for re-enrollment (first come first serve)
   */
  static async assignReEnrollment(sessionId: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/re-enrollment/assign', { sessionId })
    );
    return res.data.data;
  }

  /**
   * Mark service as ended
   */
  static async markServiceEnded(sessionId: string, reason: string, byAudit?: boolean) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/service-ended', { sessionId }),
      { reason, byAudit }
    );
    return res.data.data;
  }

  /**
   * Process AI robot result
   */
  static async processAIRobotResult(
    sessionId: string,
    result: { success: boolean; halfSuccess?: boolean; validationErrors?: Array<{ field: string; error: string }> }
  ) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/ai-robot-result', { sessionId }),
      result
    );
    return res.data.data;
  }

  /**
   * Simulate AI robot processing
   */
  static async processAIRobot(sessionId: string) {
    const res = await apiService.post<{ success: boolean; data: EnrollmentSession }>(
      buildEndpoint('/enrollments/sessions/:sessionId/process-ai', { sessionId })
    );
    return res.data.data;
  }

  /**
   * Get available actions for a session
   */
  static async getAvailableActions(sessionId: string) {
    const res = await apiService.get<{
      success: boolean;
      data: { currentStep: number; stepName: string; actions: string[] }
    }>(
      buildEndpoint('/enrollments/sessions/:sessionId/available-actions', { sessionId })
    );
    return res.data.data;
  }

  /**
   * Get agent's manager info (name and email)
   */
  static async getManagerInfo() {
    const res = await apiService.get<{
      success: boolean;
      data: { managerName: string | null; managerEmail: string | null }
    }>('/enrollments/manager-info');
    return res.data.data;
  }
}
